import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  AffiliateFilleulView,
  AffiliateProgramPublicView,
  AffiliateProfileType,
  AffiliateRecruitView,
} from "@itaas/pep-contracts";
import { AFFILIATE_PROFILE_LABELS, isInstitutionAffiliateProfile } from "@itaas/pep-contracts";
import {
  addAffiliateProducts,
  getAffiliateMe,
  getAffiliatePrograms,
  getAffiliateReferrals,
  getAffiliateRecruits,
  getAffiliateStats,
  logoutAffiliate,
  registerAffiliate,
  saveAffiliateSessionToken,
  sendAffiliateLoginCode,
  sendAffiliateEmailCode,
  checkAffiliateEmailAvailable,
  verifyAffiliateLogin,
  verifyAffiliateEmail,
} from "../api/affiliate-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { getPepSiteUrl } from "../config";
import {
  applyShareTemplate,
  buildAffiliateUrl,
  buildFacebookShareUrl,
  buildRecruitUrl,
  buildWhatsAppShareUrl,
  captureAffiliateRecruitRef,
  getOwnAffiliateCode,
  getStoredRecruiterCode,
  saveOwnAffiliateCode,
} from "../utils/affiliate-attribution";
import { clearAffiliateSessionToken, getAffiliateSessionToken } from "../utils/affiliate-session";
import { AffiliateToolkitSection } from "../components/affiliate/AffiliateToolkitSection";
import { StudioMarketingSection } from "../components/marketing/StudioMarketingSection";

const PROFILE_OPTIONS: { value: AffiliateProfileType; label: string }[] = [
  { value: "individual", label: AFFILIATE_PROFILE_LABELS.individual },
  { value: "teacher", label: AFFILIATE_PROFILE_LABELS.teacher },
  { value: "student", label: AFFILIATE_PROFILE_LABELS.student },
  { value: "parent", label: AFFILIATE_PROFILE_LABELS.parent },
  { value: "school", label: AFFILIATE_PROFILE_LABELS.school },
  { value: "hospital", label: AFFILIATE_PROFILE_LABELS.hospital },
  { value: "enterprise", label: AFFILIATE_PROFILE_LABELS.enterprise },
  { value: "other", label: AFFILIATE_PROFILE_LABELS.other },
];

export function AffiliationLandingPage() {
  const [programs, setPrograms] = useState<AffiliateProgramPublicView[]>([]);

  useEffect(() => {
    getAffiliatePrograms()
      .then((r) => setPrograms(r.programs))
      .catch(console.error);
  }, []);

  return (
    <SiteLayout>
      <SeoHead
        title="Programme ambassadeur — gagnez en recommandant ITaaS"
        description="Inscrivez-vous gratuitement, partagez votre lien sur WhatsApp ou Facebook et touchez une commission quand une école s'abonne à ITaaS Edu."
        canonicalPath="/affiliation"
        keywords={[
          "affiliation logiciel école RDC",
          "gagner argent recommandation",
          "ambassadeur ITaaS Edu",
        ]}
      />
      <div className="pep-page-hero py-5 mb-0">
        <div className="container">
          <h1 className="display-5 fw-bold mb-3">Programme ambassadeur ITaaS</h1>
          <p className="lead col-lg-9 mb-4 opacity-90">
            Recommandez les solutions ITaaS (Edu, RH, Inventory, Hospitol, Billing et bien
            d&apos;autres). Inscription gratuite, lien personnel, commission ou réduction facture
            dès qu&apos;un filleul souscrit.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <Link className="btn btn-light btn-lg fw-semibold" to="/affiliation/inscription">
              S&apos;inscrire gratuitement
            </Link>
            <Link className="btn btn-outline-light btn-lg" to="/affiliation/mon-lien">
              Connexion ambassadeur
            </Link>
            <a className="btn btn-outline-light btn-lg" href="#outils-marketing">
              Outils marketing
            </a>
          </div>
        </div>
      </div>

      <div className="container py-5">
        <section className="row g-4 mb-5">
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm p-4">
              <div className="display-6 mb-2">1</div>
              <h2 className="h5 fw-bold">Inscrivez-vous</h2>
              <p className="text-secondary small mb-0">
                Enseignant, parent, étudiant, école ou particulier — créez votre compte ambassadeur
                en 2 minutes.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm p-4">
              <div className="display-6 mb-2">2</div>
              <h2 className="h5 fw-bold">Partagez votre lien</h2>
              <p className="text-secondary small mb-0">
                WhatsApp, Facebook, bouche-à-oreille — votre lien trace chaque recommandation
                automatiquement.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm p-4">
              <div className="display-6 mb-2">3</div>
              <h2 className="h5 fw-bold">Gagnez à la conversion</h2>
              <p className="text-secondary small mb-0">
                Particuliers : commission versée par <strong>mobile money</strong> ou en{" "}
                <strong>agence ITaaS</strong>. Écoles et hôpitaux :{" "}
                <strong>réduction sur votre prochaine facture</strong> quand votre filleul paie.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-5">
          <h2 className="h4 fw-bold mb-3">Programmes ouverts ({programs.length})</h2>
          <div className="row g-3">
            {programs.map((p) => (
              <div className="col-md-6" key={p.productSlug}>
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold">{p.displayNameFr}</h3>
                    <p className="text-secondary small">{p.taglineFr}</p>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <span className="badge text-bg-primary">
                        {p.commissionPercent}% commission
                      </span>
                      <span className="badge text-bg-light border">
                        Min. paiement ${p.minPayoutUsd}
                      </span>
                      {p.allowInstitutionReferrers && (
                        <span className="badge text-bg-success">
                          Établ. : −{p.institutionReferralDiscountPercent}% facture
                        </span>
                      )}
                    </div>
                    <Link
                      className="btn btn-primary btn-sm"
                      to={`/affiliation/inscription?product=${p.productSlug}`}
                    >
                      Devenir ambassadeur →
                    </Link>
                  </div>
                </article>
              </div>
            ))}
            {programs.length === 0 && (
              <p className="text-secondary">Aucun programme ouvert pour le moment.</p>
            )}
          </div>
        </section>

        <AffiliateToolkitSection />

        <section className="p-4 rounded-4 bg-body-tertiary">
          <h2 className="h5 fw-bold mb-2">Partenaires vs ambassadeurs</h2>
          <p className="text-secondary small mb-3">
            Le programme <Link to="/partners">partenaires ITaaS</Link> s&apos;adresse aux
            intégrateurs, revendeurs et ESN avec contrat commercial. Le programme{" "}
            <strong>ambassadeur</strong> est ouvert à tous : recommandez ITaaS Edu à votre réseau
            sans engagement juridique lourd.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <Link className="btn btn-primary" to="/affiliation/inscription">
              S&apos;inscrire comme ambassadeur
            </Link>
            <Link className="btn btn-outline-primary" to="/affiliation/mon-lien">
              Connexion ambassadeur
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

export function AffiliateRegisterPage() {
  const [params] = useSearchParams();
  const preProduct = params.get("product") ?? "edu";
  const [recruiterCode] = useState(() => {
    const fromUrl = params.get("recruit");
    if (fromUrl?.trim()) {
      captureAffiliateRecruitRef(`?recruit=${encodeURIComponent(fromUrl.trim())}`, "/affiliation/inscription");
      return fromUrl.trim().toUpperCase();
    }
    return getStoredRecruiterCode();
  });
  const [programs, setPrograms] = useState<AffiliateProgramPublicView[]>([]);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(true);
  const [done, setDone] = useState<{ code: string; products: string[]; rewardType: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [devOtpHint, setDevOtpHint] = useState<string | null>(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "CD",
    country: "RDC",
    profileType: "individual" as AffiliateProfileType,
    organization: "",
    city: "",
    whatsapp: "",
    products: [preProduct],
  });

  const isInstitution = isInstitutionAffiliateProfile(form.profileType);
  const primaryProgram = programs.find((p) => form.products.includes(p.productSlug));

  useEffect(() => {
    getAffiliatePrograms()
      .then((r) => {
        setPrograms(r.programs);
        setEmailVerificationRequired(r.emailVerificationRequired);
        if (!r.emailVerificationRequired) {
          setEmailVerified(true);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (programs.length && !programs.some((p) => p.productSlug === preProduct)) {
      setForm((f) => ({ ...f, products: [programs[0].productSlug] }));
    }
  }, [programs, preProduct]);

  useEffect(() => {
    if (!emailVerificationRequired) return;
    setEmailVerified(false);
    setEmailVerificationToken(null);
    setOtpSent(false);
    setOtpCode("");
    setDevOtpHint(null);
    setEmailTaken(false);
  }, [form.email, emailVerificationRequired]);

  function checkEmailAvailability(email: string) {
    const trimmed = email.trim();
    if (!trimmed.includes("@") || trimmed.length < 5) {
      setEmailTaken(false);
      return;
    }
    setCheckingEmail(true);
    checkAffiliateEmailAvailable(trimmed)
      .then((r) => setEmailTaken(!r.available && r.reason !== "invalid"))
      .catch(() => setEmailTaken(false))
      .finally(() => setCheckingEmail(false));
  }

  function toggleProduct(slug: string) {
    setForm((f) => ({
      ...f,
      products: f.products.includes(slug)
        ? f.products.filter((p) => p !== slug)
        : [...f.products, slug],
    }));
  }

  function sendOtp() {
    if (emailTaken) return;
    setError(null);
    setSendingOtp(true);
    sendAffiliateEmailCode(form.email, form.name)
      .then((r) => {
        setOtpSent(true);
        if (r.devCode) setDevOtpHint(r.devCode);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Envoi impossible"))
      .finally(() => setSendingOtp(false));
  }

  function confirmOtp() {
    setError(null);
    setVerifyingOtp(true);
    verifyAffiliateEmail(form.email, otpCode)
      .then((r) => {
        setEmailVerificationToken(r.emailVerificationToken);
        setEmailVerified(true);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Code invalide"))
      .finally(() => setVerifyingOtp(false));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (emailVerificationRequired && (!emailVerified || !emailVerificationToken)) {
      setError("Vérifiez votre adresse email avant de continuer.");
      return;
    }
    if (emailTaken) {
      setError("Cette adresse email est déjà inscrite — connectez-vous à la place.");
      return;
    }
    if (isInstitution && !form.organization.trim()) {
      setError("Indiquez le nom de votre établissement ou entreprise.");
      return;
    }
    setError(null);
    registerAffiliate({
      ...form,
      locale: "fr",
      countryCode: form.countryCode,
      organization: form.organization || undefined,
      city: form.city || undefined,
      whatsapp: form.whatsapp || undefined,
      emailVerificationToken: emailVerificationRequired ? emailVerificationToken ?? undefined : undefined,
      recruiterCode: recruiterCode || undefined,
    })
      .then((res) => {
        saveOwnAffiliateCode(res.code);
        saveAffiliateSessionToken(res.accessToken);
        setDone({
          code: res.code,
          products: res.products,
          rewardType: isInstitution ? "invoice_discount" : "cash_commission",
        });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Inscription impossible");
      });
  }

  if (done) {
    return (
      <SiteLayout>
        <div className="container py-5 col-lg-7">
          <div className="card border-0 shadow-sm p-4 p-md-5 text-center">
            <div className="display-6 text-success mb-2">✓</div>
            <h1 className="h4 fw-bold">Bienvenue, ambassadeur ITaaS !</h1>
            <p className="text-secondary">Votre code personnel :</p>
            <p className="fs-3 fw-bold font-monospace text-primary">{done.code}</p>
            {done.rewardType === "invoice_discount" ? (
              <p className="small text-secondary">
                En tant qu&apos;établissement, vous bénéficierez d&apos;une réduction sur votre
                prochaine facture à chaque parrainage converti.
              </p>
            ) : (
              <p className="small text-secondary">
                Vos commissions seront versées sur ce numéro vérifié (mobile money ou agence ITaaS).
              </p>
            )}
            <Link className="btn btn-primary btn-lg" to="/affiliation/mon-lien">
              Voir mes liens de partage →
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <SeoHead
        title="Inscription ambassadeur ITaaS"
        description="Inscrivez-vous gratuitement au programme ambassadeur ITaaS et partagez votre lien personnel."
        canonicalPath="/affiliation/inscription"
      />
      <div className="container py-5 col-lg-8">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h1 className="display-6 fw-bold mb-2">Inscription ambassadeur</h1>
            <p className="text-secondary mb-0">
              {emailVerificationRequired
                ? "Votre email sera vérifié — vous vous connecterez ensuite avec le code reçu par mail."
                : "Inscription gratuite — partagez vos liens et gagnez des commissions sur chaque école convertie."}
            </p>
          </div>
          <Link className="btn btn-outline-primary flex-shrink-0" to="/affiliation/mon-lien">
            Déjà inscrit ? Connexion →
          </Link>
        </div>

        {recruiterCode && (
          <div className="alert alert-info py-2 small mb-4">
            Vous avez été invité par l&apos;ambassadeur{" "}
            <strong className="font-monospace">{recruiterCode}</strong>. Une fois inscrit, vos
            conversions clients lui rapporteront un override selon le programme produit.
          </div>
        )}

        <form onSubmit={onSubmit} className="card border-0 shadow-sm p-4">
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label">Nom complet *</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Profil *</label>
              <select
                className="form-select"
                value={form.profileType}
                onChange={(e) =>
                  setForm({ ...form, profileType: e.target.value as AffiliateProfileType })
                }
              >
                {PROFILE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {isInstitution && (
              <div className="col-12">
                <div className="alert alert-info py-2 small mb-0">
                  En tant qu&apos;établissement, vous ne recevez pas de cash : chaque école ou
                  hôpital parrainé vous donne{" "}
                  <strong>
                    {primaryProgram?.institutionReferralDiscountPercent ?? 10}% de réduction
                  </strong>{" "}
                  sur votre prochaine facture ITaaS.
                </div>
              </div>
            )}
            {!isInstitution && (
              <div className="col-12">
                <div className="alert alert-light border py-2 small mb-0">
                  Commission cash versée par <strong>mobile money</strong> ou retrait en{" "}
                  <strong>agence ITaaS</strong> sur le numéro vérifié ci-dessous.
                </div>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={(e) => checkEmailAvailability(e.target.value)}
                required
              />
              {checkingEmail && (
                <p className="form-text small mb-0">Vérification de l&apos;email…</p>
              )}
              {emailTaken && (
                <div className="alert alert-warning py-2 small mt-2 mb-0">
                  Cette adresse est déjà inscrite.{" "}
                  <Link to="/affiliation/mon-lien">Connectez-vous ici →</Link>
                </div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Pays *</label>
              <select
                className="form-select"
                value={form.countryCode}
                onChange={(e) => {
                  const cc = e.target.value;
                  setForm({
                    ...form,
                    countryCode: cc,
                    country: cc === "CD" ? "RDC" : cc === "CG" ? "Congo" : cc,
                  });
                }}
              >
                <option value="CD">RDC (+243)</option>
                <option value="CG">Congo (+242)</option>
                <option value="RW">Rwanda (+250)</option>
                <option value="BI">Burundi (+257)</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Téléphone mobile money *</label>
              <input
                type="tel"
                className="form-control"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+243 …"
                required
                minLength={8}
              />
            </div>
            {isInstitution && (
              <div className="col-12">
                <label className="form-label">Nom de l&apos;établissement / entreprise *</label>
                <input
                  className="form-control"
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  placeholder="Ex. École La Réussite, Clinique Saint-Joseph…"
                  required
                />
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">WhatsApp (optionnel)</label>
              <input
                type="tel"
                className="form-control"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Ville</label>
              <input
                className="form-control"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
          </div>

          {emailVerificationRequired && (
            <div className="border rounded-3 p-3 mb-4 bg-body-tertiary">
              <h2 className="h6 fw-bold mb-3">Vérification de l&apos;email *</h2>
              {emailVerified ? (
                <div className="text-success small fw-semibold">
                  ✓ Email vérifié — vous pourrez vous connecter avec le code reçu par mail
                </div>
              ) : (
                <>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={sendOtp}
                      disabled={sendingOtp || !form.email.includes("@") || emailTaken}
                    >
                      {sendingOtp ? "Envoi…" : otpSent ? "Renvoyer le code" : "Recevoir le code par email"}
                    </button>
                  </div>
                  {devOtpHint && (
                    <div className="alert alert-warning py-2 small">
                      Mode développement — code email : <strong>{devOtpHint}</strong>
                    </div>
                  )}
                  {otpSent && (
                    <div className="row g-2 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label small">Code à 6 chiffres</label>
                        <input
                          className="form-control"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="123456"
                          inputMode="numeric"
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={confirmOtp}
                          disabled={verifyingOtp || otpCode.length < 6}
                        >
                          {verifyingOtp ? "…" : "Valider"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <h2 className="h6 fw-bold mb-3">Produits à promouvoir</h2>
          <div className="row g-2 mb-4" style={{ maxHeight: "22rem", overflowY: "auto" }}>
            {programs.map((p) => (
              <div className="col-sm-6 col-lg-4" key={p.productSlug}>
                <label className="d-flex gap-2 p-3 border rounded-3 w-100">
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    checked={form.products.includes(p.productSlug)}
                    onChange={() => toggleProduct(p.productSlug)}
                  />
                  <span>
                    <span className="fw-semibold">{p.displayNameFr}</span>
                    <span className="d-block small text-secondary">
                      {p.commissionPercent}% cash · −{p.institutionReferralDiscountPercent}% facture
                      établ.
                    </span>
                  </span>
                </label>
              </div>
            ))}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          <button
            type="submit"
            className="btn btn-primary btn-lg w-100"
            disabled={!form.products.length || (emailVerificationRequired && !emailVerified) || emailTaken}
          >
            Créer mon compte ambassadeur →
          </button>
        </form>
      </div>
    </SiteLayout>
  );
}

export function AffiliateDashboardPage() {
  const [loginInput, setLoginInput] = useState(getOwnAffiliateCode() ?? "");
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getAffiliateStats>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [programs, setPrograms] = useState<AffiliateProgramPublicView[]>([]);
  const [selectedNewProducts, setSelectedNewProducts] = useState<string[]>([]);
  const [addingProducts, setAddingProducts] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loginStep, setLoginStep] = useState<"identify" | "otp">("identify");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devOtpHint, setDevOtpHint] = useState<string | null>(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [loginHint, setLoginHint] = useState<string | null>(null);
  const [filleuls, setFilleuls] = useState<AffiliateFilleulView[]>([]);
  const [recruits, setRecruits] = useState<AffiliateRecruitView[]>([]);

  const FILLEUL_STAGE_LABEL: Record<AffiliateFilleulView["stage"], string> = {
    lead: "Lead enregistré",
    provisioned: "Compte activé",
    converted: "Commission validée",
  };

  const FILLEUL_STAGE_BADGE: Record<AffiliateFilleulView["stage"], string> = {
    lead: "text-bg-secondary",
    provisioned: "text-bg-primary",
    converted: "text-bg-success",
  };

  function loadFilleuls() {
    return getAffiliateReferrals(50)
      .then((r) => setFilleuls(r.items))
      .catch(() => setFilleuls([]));
  }

  function loadRecruits() {
    return getAffiliateRecruits(50)
      .then((r) => setRecruits(r.items))
      .catch(() => setRecruits([]));
  }

  function loadDownline() {
    return Promise.all([loadFilleuls(), loadRecruits()]);
  }

  useEffect(() => {
    getAffiliatePrograms()
      .then((r) => setPrograms(r.programs))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!getAffiliateSessionToken()) return;
    getAffiliateMe()
      .then((s) => {
        setStats(s);
        setAuthenticated(true);
        saveOwnAffiliateCode(s.code);
        setLoginInput(s.code);
        void loadDownline();
      })
      .catch(() => clearAffiliateSessionToken());
  }, []);

  function loadStatsFromSession() {
    setError(null);
    return getAffiliateMe()
      .then((s) => {
        setStats(s);
        setAuthenticated(true);
        saveOwnAffiliateCode(s.code);
        return loadDownline();
      })
      .catch((err) => {
        clearAffiliateSessionToken();
        setAuthenticated(false);
        setStats(null);
        setError(err instanceof Error ? err.message : "Session expirée");
      });
  }

  function sendLoginOtp() {
    setError(null);
    setSendingOtp(true);
    sendAffiliateLoginCode(loginInput.trim())
      .then((r) => {
        setOtpSent(true);
        setLoginStep("otp");
        setLoginHint(r.loginHint ?? null);
        if (r.devCode) setDevOtpHint(r.devCode);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Envoi impossible"))
      .finally(() => setSendingOtp(false));
  }

  function verifyLoginOtp() {
    setError(null);
    setVerifyingOtp(true);
    verifyAffiliateLogin(loginInput.trim(), otpCode)
      .then((r) => {
        saveAffiliateSessionToken(r.accessToken);
        setStats(r.stats);
        setAuthenticated(true);
        saveOwnAffiliateCode(r.stats.code);
        setLoginStep("identify");
        setOtpCode("");
        setOtpSent(false);
        setDevOtpHint(null);
        void loadDownline();
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Code invalide"))
      .finally(() => setVerifyingOtp(false));
  }

  function handleLogout() {
    void logoutAffiliate().finally(() => {
      clearAffiliateSessionToken();
      setAuthenticated(false);
      setStats(null);
      setLoginStep("identify");
      setOtpSent(false);
      setOtpCode("");
    });
  }

  function addProducts() {
    if (!stats || !selectedNewProducts.length) return;
    setAddingProducts(true);
    setError(null);
    addAffiliateProducts(selectedNewProducts)
      .then(() => {
        setSelectedNewProducts([]);
        void loadStatsFromSession();
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Impossible d'ajouter ces produits"),
      )
      .finally(() => setAddingProducts(false));
  }

  const EVENT_LABELS: Record<string, string> = {
    conversion_earned: "Commission gagnée",
    override_earned: "Override two-tier",
    payout: "Versement reçu",
    invoice_discount_pending: "Réduction facture en attente",
  };

  const primaryProduct = stats?.products[0] ?? "edu";
  const pepSiteUrl = getPepSiteUrl();
  const shareLink = useMemo(() => {
    if (!stats) return "";
    return buildAffiliateUrl(pepSiteUrl, `/products/${primaryProduct}`, stats.code, {
      products: primaryProduct,
    });
  }, [stats, primaryProduct, pepSiteUrl]);

  const trialLink = useMemo(() => {
    if (!stats) return "";
    return buildAffiliateUrl(pepSiteUrl, "/configure", stats.code, {
      products: stats.products.join(","),
      product: primaryProduct,
    });
  }, [stats, primaryProduct, pepSiteUrl]);

  const recruitLink = useMemo(() => {
    if (!stats) return "";
    return buildRecruitUrl(pepSiteUrl, stats.code);
  }, [stats, pepSiteUrl]);

  const whatsappUrl = useMemo(() => {
    if (!shareLink) return "";
    const program = programs.find((p) => p.productSlug === primaryProduct);
    const template =
      program?.shareMessageFr ??
      `Découvrez ITaaS ${primaryProduct.toUpperCase()}. Créez votre environnement : {{link}}`;
    const msg = applyShareTemplate(template, shareLink);
    return buildWhatsAppShareUrl(msg);
  }, [shareLink, programs, primaryProduct]);

  return (
    <SiteLayout>
      <SeoHead
        title="Mon lien ambassadeur"
        description="Espace ambassadeur ITaaS : lien de parrainage, statistiques et suivi de vos filleuls."
        canonicalPath="/affiliation/mon-lien"
        noIndex
      />
      <div className="container py-5 col-lg-8">
        <h1 className="display-6 fw-bold mb-2">Mon espace ambassadeur</h1>
        <p className="text-secondary mb-4">
          Connectez-vous avec votre <strong>code ambassadeur</strong> ou votre{" "}
          <strong>adresse email</strong> — validation par email, sans mot de passe.
        </p>

        {!authenticated && (
          <div className="card border-0 shadow-sm p-4 mb-4">
            {loginStep === "identify" ? (
              <>
                <label className="form-label fw-semibold">Code ou email</label>
                <div className="input-group mb-2">
                  <input
                    className="form-control font-monospace"
                    value={loginInput}
                    onChange={(e) => setLoginInput(e.target.value)}
                    placeholder="EDU-ABC123 ou vous@email.com"
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!loginInput.trim() || sendingOtp}
                    onClick={sendLoginOtp}
                  >
                    {sendingOtp ? "Envoi…" : otpSent ? "Renvoyer le code" : "Recevoir le code par email"}
                  </button>
                </div>
                <p className="form-text mb-0">
                  Si vous saisissez votre code, l&apos;email part vers l&apos;adresse enregistrée à
                  l&apos;inscription.
                </p>
              </>
            ) : (
              <>
                {loginHint && <p className="small text-secondary mb-2">{loginHint}</p>}
                {devOtpHint && (
                  <div className="alert alert-warning py-2 small">
                    Mode développement — code email : <strong>{devOtpHint}</strong>
                  </div>
                )}
                <label className="form-label fw-semibold">Code email à 6 chiffres</label>
                <div className="input-group mb-2">
                  <input
                    className="form-control"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={otpCode.length < 4 || verifyingOtp}
                    onClick={verifyLoginOtp}
                  >
                    {verifyingOtp ? "Vérification…" : "Se connecter"}
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => {
                    setLoginStep("identify");
                    setOtpSent(false);
                    setOtpCode("");
                  }}
                >
                  ← Changer d&apos;identifiant
                </button>
              </>
            )}
            {error && <div className="text-danger small mt-2">{error}</div>}
          </div>
        )}

        {authenticated && stats && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="small text-secondary mb-0">
              Connecté — <strong>{stats.name}</strong> · <code>{stats.code}</code>
            </p>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
              Se déconnecter
            </button>
          </div>
        )}

        {stats && (
          <>
            <div className="card border-0 shadow-sm p-4 mb-4 border-start border-4 border-primary">
              <h2 className="h5 fw-bold mb-2">Mon lien de parrainage</h2>
              <p className="small text-secondary mb-3">
                Envoyez ce lien à un établissement — le paramètre <code>ref={stats.code}</code>{" "}
                vous crédite automatiquement comme parrain.
              </p>
              <label className="form-label small fw-semibold text-primary mb-1">
                Créer mon environnement (recommandé)
              </label>
              <div className="input-group mb-3">
                <input className="form-control font-monospace small" readOnly value={trialLink} />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void navigator.clipboard.writeText(trialLink)}
                >
                  Copier le lien
                </button>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <a
                  className="btn btn-success btn-sm"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partager sur WhatsApp
                </a>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => void navigator.clipboard.writeText(trialLink)}
                >
                  Copier à nouveau
                </button>
              </div>
            </div>

            {stats.status === "suspended" && (
              <div className="alert alert-danger mb-4">
                Votre compte ambassadeur est <strong>suspendu</strong>. Vos liens ne génèrent plus
                de nouvelles commissions. Contactez{" "}
                <a href="mailto:contact@itaas-rdc.com">contact@itaas-rdc.com</a>.
              </div>
            )}

            {stats.rewardType === "invoice_discount" ? (
              <div className="alert alert-info small mb-4">
                Récompense établissement : <strong>−{stats.invoiceDiscountPercent ?? 10}%</strong>{" "}
                sur votre prochaine facture ITaaS à chaque parrainage converti
                {stats.organization ? ` (${stats.organization})` : ""}.
                Réductions en attente : <strong>{stats.pendingInvoiceDiscounts}</strong>.
              </div>
            ) : (
              <div className="card border-0 shadow-sm p-4 mb-4 border-start border-4 border-warning">
                <h2 className="h6 fw-bold mb-3">Mes gains (cash)</h2>
                <div className="row g-3 text-center">
                  <div className="col-md-4">
                    <div className="fs-4 fw-bold text-warning">
                      ${stats.pendingCashUsd.toFixed(2)}
                    </div>
                    <div className="small text-secondary">En attente de paiement</div>
                  </div>
                  <div className="col-md-4">
                    <div className="fs-4 fw-bold text-success">${stats.paidCashUsd.toFixed(2)}</div>
                    <div className="small text-secondary">Déjà versé</div>
                  </div>
                  <div className="col-md-4">
                    <div className="fs-5 fw-semibold">${stats.minPayoutUsd}</div>
                    <div className="small text-secondary">Seuil minimum de retrait</div>
                  </div>
                </div>
                {stats.payoutReady ? (
                  <p className="small text-success mb-0 mt-3">
                    ✓ Seuil atteint — notre équipe procède au versement mobile money ou en agence.
                  </p>
                ) : (
                  <p className="small text-secondary mb-0 mt-3">
                    Le versement est déclenché dès que le solde atteint ${stats.minPayoutUsd} (mobile
                    money ou agence ITaaS).
                  </p>
                )}
              </div>
            )}

            <div className="mb-3 small text-secondary">
              Produits promus : <strong>{stats.products.join(", ")}</strong>
            </div>

            {stats.availableProductSlugs.length > 0 && stats.status === "active" && (
              <div className="card border-0 shadow-sm p-4 mb-4">
                <h2 className="h6 fw-bold mb-3">Ajouter un produit à promouvoir</h2>
                <div className="d-flex flex-column gap-2 mb-3">
                  {stats.availableProductSlugs.map((slug) => {
                    const label = programs.find((p) => p.productSlug === slug)?.displayNameFr ?? slug;
                    return (
                      <label key={slug} className="d-flex align-items-center gap-2 mb-0">
                        <input
                          type="checkbox"
                          checked={selectedNewProducts.includes(slug)}
                          onChange={() =>
                            setSelectedNewProducts((prev) =>
                              prev.includes(slug)
                                ? prev.filter((s) => s !== slug)
                                : [...prev, slug],
                            )
                          }
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!selectedNewProducts.length || addingProducts}
                  onClick={addProducts}
                >
                  {addingProducts ? "Ajout…" : "Activer les produits sélectionnés"}
                </button>
              </div>
            )}
            <div className="row g-3 mb-4">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm p-3 text-center">
                  <div className="fs-4 fw-bold text-primary">{stats.referralCount}</div>
                  <div className="small text-secondary">Leads / parrainages</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm p-3 text-center">
                  <div className="fs-4 fw-bold text-success">{stats.conversionCount}</div>
                  <div className="small text-secondary">Conversions validées</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm p-3 text-center">
                  <div className="fs-4 fw-bold text-info">{stats.recruitCount ?? 0}</div>
                  <div className="small text-secondary">Ambassadeurs recrutés</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm p-3 text-center">
                  <div className="small text-secondary">Membre depuis</div>
                  <div className="fw-semibold">{new Date(stats.createdAt).toLocaleDateString("fr-FR")}</div>
                </div>
              </div>
            </div>
            {stats.parentAffiliateCode && (
              <p className="small text-secondary mb-4">
                Parrain ambassadeur :{" "}
                <code className="font-monospace">{stats.parentAffiliateCode}</code>
              </p>
            )}

            <div className="card border-0 shadow-sm p-4 mb-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h2 className="h6 fw-bold mb-0">Mes filleuls</h2>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => void loadDownline()}
                >
                  Actualiser
                </button>
              </div>
              {filleuls.length === 0 ? (
                <p className="text-secondary small mb-0">
                  Aucun filleul pour le moment. Partagez votre lien d&apos;essai — dès qu&apos;un
                  établissement s&apos;inscrit et active son compte, il apparaît ici.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Établissement</th>
                        <th>Contact</th>
                        <th>Produits</th>
                        <th>Étape</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filleuls.map((f) => (
                        <tr key={f.id}>
                          <td className="small text-nowrap">
                            {new Date(f.createdAt).toLocaleDateString("fr-FR")}
                          </td>
                          <td>{f.company}</td>
                          <td className="small">
                            {f.contactLabel}
                            <br />
                            <span className="text-secondary">{f.emailMasked}</span>
                          </td>
                          <td className="small">{f.products.join(", ") || "—"}</td>
                          <td>
                            <span className={`badge ${FILLEUL_STAGE_BADGE[f.stage]}`}>
                              {FILLEUL_STAGE_LABEL[f.stage]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card border-0 shadow-sm p-4 mb-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h2 className="h6 fw-bold mb-0">Mes ambassadeurs recrutés</h2>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => void loadRecruits()}
                >
                  Actualiser
                </button>
              </div>
              <p className="small text-secondary mb-3">
                Partagez votre lien de recrutement — quand un ambassadeur recruté convertit un
                client, vous touchez un override two-tier (taux configurable par produit).
              </p>
              {recruits.length === 0 ? (
                <p className="text-secondary small mb-0">
                  Aucun ambassadeur recruté pour le moment. Utilisez le lien{" "}
                  <strong>Recruter un ambassadeur</strong> ci-dessous.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Recruté le</th>
                        <th>Ambassadeur</th>
                        <th>Produits</th>
                        <th>Leads</th>
                        <th>Conv.</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recruits.map((r) => (
                        <tr key={r.id}>
                          <td className="small text-nowrap">
                            {new Date(r.recruitedAt).toLocaleDateString("fr-FR")}
                          </td>
                          <td>
                            {r.name}
                            <div className="small text-secondary font-monospace">{r.code}</div>
                          </td>
                          <td className="small">{r.products.join(", ") || "—"}</td>
                          <td>{r.referralCount}</td>
                          <td>{r.conversionCount}</td>
                          <td>
                            <span
                              className={`badge ${r.status === "active" ? "text-bg-success" : "text-bg-danger"}`}
                            >
                              {r.status === "active" ? "Actif" : "Suspendu"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card border-0 shadow-sm p-4 mb-4">
              <h2 className="h6 fw-bold mb-3">Liens à partager</h2>
              <p className="small text-secondary mb-3">
                <strong>Clients</strong> — lien essai gratuit avec votre code <code>{stats.code}</code>.
                {" "}<strong>Ambassadeurs</strong> — lien d&apos;inscription avec recrutement two-tier.
              </p>
              <div className="mb-3">
                <label className="form-label small fw-semibold text-primary">
                  Recruter un ambassadeur — lien two-tier
                </label>
                <div className="input-group input-group-sm">
                  <input className="form-control font-monospace" readOnly value={recruitLink} />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => void navigator.clipboard.writeText(recruitLink)}
                  >
                    Copier
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small fw-semibold text-primary">
                  Lien Configure — à envoyer à vos filleuls
                </label>
                <div className="input-group input-group-sm">
                  <input className="form-control font-monospace" readOnly value={trialLink} />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => void navigator.clipboard.writeText(trialLink)}
                  >
                    Copier
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small">Page produit (présentation)</label>
                <div className="input-group input-group-sm">
                  <input className="form-control font-monospace" readOnly value={shareLink} />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => void navigator.clipboard.writeText(shareLink)}
                  >
                    Copier
                  </button>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <a
                  className="btn btn-success"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partager sur WhatsApp
                </a>
                <a
                  className="btn btn-primary"
                  href={buildFacebookShareUrl(shareLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partager sur Facebook
                </a>
              </div>
            </div>

            <div className="card border-0 shadow-sm p-4 mb-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h2 className="h6 fw-bold mb-0">Kit campagne — assets à partager</h2>
                <Link className="btn btn-sm btn-outline-primary" to="/affiliation#outils-marketing">
                  Kit complet →
                </Link>
              </div>
              <StudioMarketingSection productSlug={primaryProduct} hideHeader compact />
            </div>

            {stats.recentEvents.length > 0 && (
              <div className="card border-0 shadow-sm p-4 mb-4">
                <h2 className="h6 fw-bold mb-3">Historique des commissions</h2>
                <ul className="list-unstyled small mb-0">
                  {stats.recentEvents.map((ev) => (
                    <li key={ev.id} className="d-flex justify-content-between border-bottom py-2">
                      <span>
                        {EVENT_LABELS[ev.type] ?? ev.type}
                        {ev.productSlug ? ` · ${ev.productSlug}` : ""}
                        {ev.fromAffiliateCode ? ` · ${ev.fromAffiliateCode}` : ""}
                        {ev.note ? ` — ${ev.note}` : ""}
                      </span>
                      <span className="text-nowrap ms-2 text-secondary">
                        {ev.amountUsd != null ? `$${ev.amountUsd.toFixed(2)}` : ""}{" "}
                        {new Date(ev.at).toLocaleDateString("fr-FR")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="small text-secondary">
              Les commissions sont versées après validation du premier paiement de l&apos;école
              référée. Contactez notre équipe commerciale pour le suivi de vos gains.
            </p>
          </>
        )}

        <Link to="/affiliation">← Programme ambassadeur</Link>
        {!authenticated && (
          <>
            <span className="text-secondary mx-2">·</span>
            <Link to="/affiliation/inscription">Pas encore inscrit ?</Link>
          </>
        )}
      </div>
    </SiteLayout>
  );
}
